import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { finishKitchenTicket, setKitchenItem, subscribeKitchen } from '../services/kitchen'
import { servedOf } from '../utils/serving'
import type { KitchenTicket, OrderLine } from '../types'

/** Sipariş satırının mutfak fişindeki kimliği (supabase kitchen_line_key ile aynı). */
export const kitchenKey = (l: OrderLine) =>
  [l.productId, l.note ?? '', l.byName ?? '', l.weigh?.id ?? ''].join('|')

/**
 * Götürülen adetle kapanacak mutfak kalemleri: en eski fişten başlayarak,
 * adedi götürülenle tamamen karşılanan ve henüz hazır olmayanlar. `all`:
 * fişin bütün kalemleri bitiyor, fiş tek seferde kapatılabilir.
 */
export function kitchenToClose(
  tickets: KitchenTicket[],
  orderId: string,
  lines: OrderLine[],
): { ticketId: string; items: number[]; all: boolean }[] {
  const served = new Map<string, number>()
  for (const l of lines) {
    const k = kitchenKey(l)
    served.set(k, (served.get(k) ?? 0) + servedOf(l))
  }
  const used = new Map<string, number>()
  const out: { ticketId: string; items: number[]; all: boolean }[] = []
  const mine = tickets
    .filter((t) => t.orderId === orderId && t.status !== 'void')
    .sort((a, b) => a.createdAt - b.createdAt)
  for (const t of mine) {
    const items: number[] = []
    t.items.forEach((it, i) => {
      const before = used.get(it.key) ?? 0
      used.set(it.key, before + it.qty)
      if (t.status === 'open' && it.status !== 'ready' && before + it.qty <= (served.get(it.key) ?? 0)) {
        items.push(i)
      }
    })
    if (items.length === 0) continue
    out.push({ ticketId: t.id, items, all: t.items.every((it, i) => it.status === 'ready' || items.includes(i)) })
  }
  return out
}

/**
 * Sipariş takip: mutfağın "hazır" dediği, garsonun henüz götürmediği adet
 * (kullanıcı, 11 Eki 2026). Götürülen adetler en eski hazırlar sayılıyor:
 * hazır toplamından götürülen düşülüyor.
 */
export function useKitchenReady(enabled: boolean) {
  const { tenantId } = useAuth()
  const [tickets, setTickets] = useState<KitchenTicket[]>([])

  useEffect(() => {
    if (!enabled || !tenantId) return
    return subscribeKitchen(tenantId, setTickets)
  }, [enabled, tenantId])

  const ready = useMemo(() => {
    const m = new Map<string, number>()
    for (const t of tickets) {
      if (t.status === 'void') continue
      for (const it of t.items) {
        if (it.status !== 'ready') continue
        const k = `${t.orderId}::${it.key}`
        m.set(k, (m.get(k) ?? 0) + it.qty)
      }
    }
    return m
  }, [tickets])

  /** Bu satırın hazır olup henüz götürülmemiş adedi. */
  const readyOf = (orderId: string, line: OrderLine, waiting: number) =>
    Math.max(0, Math.min(waiting, (ready.get(`${orderId}::${kitchenKey(line)}`) ?? 0) - servedOf(line)))

  /**
   * Garson "götürdüm" deyince mutfak ekranından da kalksın (kullanıcı, 22 Eki
   * 2026). Götürülen adet en eski fişlerden başlayarak dağıtılıyor; adedi
   * götürülenle TAMAMEN karşılanan kalem "hazır" yapılıyor, bütün kalemleri
   * biten fiş kapanıyor. Yarısı götürülen kalem (2 Adana'dan 1'i) mutfakta
   * kalıyor: yoksa kalan Adana garsona "hazır" görünürdü.
   *
   * `lines` işaretlemeden SONRAKİ satırlar. Hata garsonun işini durdurmuyor,
   * mutfak fişi elle de kapatılabilir.
   */
  const closeServed = async (orderId: string, lines: OrderLine[]) => {
    for (const { ticketId, items, all } of kitchenToClose(tickets, orderId, lines)) {
      try {
        if (all) await finishKitchenTicket(ticketId)
        else for (const i of items) await setKitchenItem(ticketId, i, 'ready')
      } catch (err) {
        console.error('[takip] mutfak fişi kapatılamadı', err)
      }
    }
  }

  return { readyOf, closeServed }
}
